'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { Check, ExternalLink, Loader2 } from 'lucide-react'
import { useCapabilityGate } from '@/hooks/use-can'
import type {
  IntegrationMeta,
  IntegrationProvider,
} from '@/lib/integrations/catalog'

/**
 * Shared chrome for the integration detail page's primary CTA.
 *
 * Historically this file owned a sticky right-rail that contained the
 * connect button, a status pill, a `Details` description list (Type /
 * Direction / Category / Pricing / Author), and a `Resources` list of
 * outbound links. The sidebar packed too much filler around the one
 * thing users actually came to do — connect — so the page was
 * restructured into an Attio-style two-column layout (left meta rail
 * + right tabbed content) with the CTA in the hero top-right. The
 * `Details` / `Resources` content now lives inline in `MetaRail`
 * inside `integration-page.tsx`.
 *
 * What remains here is the primitives the new layout still composes:
 *
 *  - `CtaBlock` — the primary connect / disconnect / coming-soon
 *    button. Rendered inline in the hero top-right as the single
 *    connect action for the page. (It is no longer mirrored into the
 *    app-shell header — that duplicated the hero CTA and was removed.)
 *    Connect and Disconnect need `integrations.manage`, so a viewer
 *    sees the button disabled with the server's refusal copy as its
 *    title instead of learning on click.
 *  - `StatusPill` — the verified-check + bordered tag rendered next
 *    to the integration name in the hero.
 *  - `PROVIDER_CATEGORY` — the category map, also consumed by
 *    `IntegrationFooter` (now unrendered but kept on disk for
 *    backward compat) and by the `MetaRail` Category row.
 *
 * The file name is kept for now to minimise churn in unrelated imports.
 */

const TYPE_LABEL: Record<IntegrationMeta['integrationType'], string> = {
  form_webhook: 'Form webhook',
  oauth: 'OAuth',
  api_key: 'API key',
  dual_api_key: 'API key pair',
  webhook_signing_secret: 'Webhook secret',
  shared_secret: 'Shared secret',
}

const DIRECTION_LABEL: Record<IntegrationMeta['dataDirection'], string> = {
  data_in: 'Data in',
  data_out: 'Data out',
}

export const PROVIDER_CATEGORY: Record<IntegrationProvider, string> = {
  braze: 'Marketing automation',
  brevo: 'Marketing automation',
  clerk: 'Authentication',
  customerio: 'Marketing automation',
  iterable: 'Marketing automation',
  klaviyo: 'Marketing automation',
  mailchimp: 'Marketing automation',
  hubspot: 'CRM',
  mailgun: 'Email delivery',
  mailjet: 'Email delivery',
  postmark: 'Email delivery',
  sendgrid: 'Email delivery',
  onesignal: 'Push notifications',
  stripe: 'Payments',
  figma: 'Design',
  typeform: 'Forms',
  framer: 'Forms',
  slack: 'Chat',
  stytch: 'Authentication',
  supabase: 'Database',
  workos: 'Authentication',
  shopify: 'Commerce',
  revenuecat: 'Subscriptions',
}

export { DIRECTION_LABEL, TYPE_LABEL }

/**
 * Status indicator for the integration hero. Modeled on the
 * "verified-checkmark + tag" pattern used by app marketplaces (Attio,
 * Slack App Directory): a small green verified circle when the
 * integration is connected, paired with a bordered pill that names
 * the state. Available + coming-soon render as just the pill (no
 * verified circle).
 */
export function StatusPill({
  kind,
  className,
}: {
  kind:
    | 'connected'
    | 'pending_verification'
    | 'error'
    | 'available'
    | 'coming_soon'
  className?: string
}) {
  if (kind === 'connected') {
    return (
      <span className={cn('flex items-center gap-1.5', className)}>
        <span
          className="flex size-4 items-center justify-center rounded-lg bg-emerald-500"
          aria-hidden
        >
          <Check className="size-2.5 text-white" strokeWidth={3.5} />
        </span>
        <span className="flex h-5 items-center rounded-sm border border-emerald-500/40 bg-emerald-500/8 px-1.5 font-medium text-caption text-emerald-700 dark:text-emerald-400">
          Connected
        </span>
      </span>
    )
  }
  if (kind === 'coming_soon') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 font-medium text-amber-700 text-caption dark:text-amber-400',
          className
        )}
      >
        Coming soon
      </span>
    )
  }
  if (kind === 'pending_verification') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 font-medium text-amber-700 text-caption dark:text-amber-400',
          className
        )}
      >
        Waiting for test event
      </span>
    )
  }
  if (kind === 'error') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-red-500/40 bg-red-500/10 px-1.5 font-medium text-caption text-red-700 dark:text-red-400',
          className
        )}
      >
        Needs attention
      </span>
    )
  }
  return (
    <span
      className={cn(
        'flex h-5 items-center rounded-sm border border-border px-1.5 font-medium text-caption text-muted-foreground',
        className
      )}
    >
      Available
    </span>
  )
}

interface CtaBlockProps {
  integration: IntegrationMeta
  isConnected: boolean
  isPendingVerification?: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isOAuthProvider: boolean
  onConnectClick: () => void
  onDisconnectClick: () => void
  /**
   * Visual variant. The hero uses the default (`h-9`, `text-sm`)
   * for presence. The `'compact'` variant (`h-8`, `text-xs`) is a
   * slimmer button kept for reuse in tight strips.
   */
  size?: 'default' | 'compact'
  /**
   * Optional override that disables the connect button regardless of
   * the in-flight `isConnecting` state. Used by the integration
   * detail page to hold an API-key Connect button disabled until the
   * inline tutorial form is fully valid — keeps the CTA in a single
   * stable location instead of duplicating it at the bottom of the
   * form.
   */
  isDisabled?: boolean
  className?: string
}

export function CtaBlock(props: CtaBlockProps) {
  const manageGate = useCapabilityGate('integrations.manage')
  const {
    integration,
    isConnected,
    isPendingVerification,
    isConnecting,
    isDisconnecting,
    isOAuthProvider,
    onConnectClick,
    onDisconnectClick,
    size = 'default',
    isDisabled,
    className,
  } = props

  // Subtle, settings-style buttons — not the fully-rounded marketing
  // pill we used previously. Matches the SaaS settings-page reference
  // (small, `rounded-lg`, modest padding) so the CTA reads as a
  // utility action rather than a hero call-to-action.
  const sizeClasses =
    size === 'compact'
      ? 'h-8 rounded-lg px-3 text-xs'
      : 'h-9 rounded-lg px-4 text-sm'

  if (integration.availability === 'coming_soon') {
    return (
      <Button
        disabled
        variant="secondary"
        className={cn('font-medium', sizeClasses, className)}
      >
        Coming soon
      </Button>
    )
  }

  if (isConnected || isPendingVerification) {
    return (
      <Button
        variant="outline"
        className={cn('font-medium', sizeClasses, className)}
        onClick={onDisconnectClick}
        disabled={isDisconnecting || !manageGate.isAllowed}
        title={manageGate.reason ?? undefined}
      >
        {isDisconnecting ? (
          <>
            <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
            Disconnecting…
          </>
        ) : (
          'Disconnect'
        )}
      </Button>
    )
  }

  return (
    <Button
      className={cn('font-medium', sizeClasses, className)}
      onClick={onConnectClick}
      disabled={isConnecting || isDisabled === true || !manageGate.isAllowed}
      title={manageGate.reason ?? undefined}
    >
      <ConnectButtonLabel
        isConnecting={isConnecting}
        isOAuthProvider={isOAuthProvider}
      />
    </Button>
  )
}

function ConnectButtonLabel({
  isConnecting,
  isOAuthProvider,
}: {
  isConnecting: boolean
  isOAuthProvider: boolean
}) {
  if (isConnecting) {
    return (
      <>
        <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
        Connecting…
      </>
    )
  }
  if (isOAuthProvider) {
    return (
      <>
        <ExternalLink className="mr-1.5 size-3.5" aria-hidden />
        Install with OAuth
      </>
    )
  }
  return <>Connect</>
}
